import { Box, Heading, HStack, Pressable, Text, useToken, VStack } from 'native-base';
import React, { ReactElement, ReactNode } from 'react';

import HTML from 'app/components/HTML';
import {
  CardActionArrow,
  ManualAddTickets,
  PurchaseTickets,
  TryDifferentEmail,
} from 'app/components/icons';
import { useI18n } from 'app/providers/I18nProvider';
import { tID } from 'app/services/TestHelper';

export type PurchasesEmptyStateProps = {
  bottomContent?: ReactNode;
  email: string | null;
  onTryDifferentEmail: () => void;
  onManualAdd: () => void;
  onPurchaseTickets: () => void;
};

type PurchasesEmptyStateActionCardProps = {
  icon: ReactElement;
  title: string;
  subtitle: string;
  onPress: () => void;
  testID: string;
};

function PurchasesEmptyStateActionCard({
  icon,
  title,
  subtitle,
  onPress,
  testID,
}: PurchasesEmptyStateActionCardProps) {
  return (
    <Pressable testID={tID(testID)} w="full" onPress={onPress}>
      <Box
        bg="white"
        borderColor="coolGray.200"
        borderWidth="1"
        px="4"
        py="4"
        rounded="md"
        shadow="1"
      >
        <HStack alignItems="flex-start" space="3">
          <Box alignItems="center" justifyContent="center" minW="8" pt="0.5" w="8">
            {icon}
          </Box>

          <VStack flex="1" space="1">
            <Text bold color="brand.secondary">
              {title}
            </Text>
            <Text color="coolGray.500" fontSize="sm" lineHeight="sm">
              {subtitle}
            </Text>
          </VStack>

          <Box alignSelf="center">
            <CardActionArrow size={34} />
          </Box>
        </HStack>
      </Box>
    </Pressable>
  );
}

export default function PurchasesEmptyState({
  bottomContent,
  email,
  onTryDifferentEmail,
  onManualAdd,
  onPurchaseTickets,
}: PurchasesEmptyStateProps) {
  const { t } = useI18n();
  const primaryColor = useToken('colors', 'brand.primary');

  return (
    <VStack flex={1} minH="full" testID={tID('PurchasesEmptyState')}>
      <VStack space="5">
        <VStack space="3">
          <Heading fontSize="lg">{t<string>('app_auth_tickets_not_found_title')}</Heading>
          <Box testID={tID('SignInConfirmationMessage')}>
            <HTML html={t<string>('app_auth_tickets_not_found', { email: email ?? '' })} />
          </Box>
          <Text color="brand.secondary" fontWeight="semibold">
            {t<string>('app_auth_tickets_not_found_subheader')}
          </Text>
        </VStack>

        <VStack space="3">
          <PurchasesEmptyStateActionCard
            icon={<TryDifferentEmail color={primaryColor} size={20} />}
            subtitle={t<string>('app_auth_try_different_email_subtitle')}
            testID="TryDifferentEmailCard"
            title={t<string>('app_auth_try_different_email')}
            onPress={onTryDifferentEmail}
          />

          <PurchasesEmptyStateActionCard
            icon={<ManualAddTickets color={primaryColor} size={20} />}
            subtitle={t<string>('app_auth_add_manual_tickets_subtitle')}
            testID="ManuallyAddTicketsButton"
            title={t<string>('app_auth_add_manual_tickets')}
            onPress={onManualAdd}
          />

          <PurchasesEmptyStateActionCard
            icon={<PurchaseTickets color={primaryColor} size={22} />}
            subtitle={t<string>('app_auth_purchase_tickets_subtitle')}
            testID="PurchaseTicketsCard"
            title={t<string>('app_auth_purchase_tickets')}
            onPress={onPurchaseTickets}
          />
        </VStack>
      </VStack>

      <VStack mt="auto" pb="2" pt="20" space="4">
        <Box alignItems="center" w="full">
          <HTML
            color="coolGray.500"
            defaultTextProps={{
              style: {
                width: '100%',
              },
            }}
            fontSize="sm"
            html={t<string>('app_auth_tickets_not_found_footer')}
            textAlign="center"
          />
        </Box>

        {bottomContent}
      </VStack>
    </VStack>
  );
}
